import React from 'react';
import styled, { css } from 'styled-components';

import Row from 'components/Common/Row';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

export type ReviewStep = 1 | 2 | 3;

const STEPS: { step: ReviewStep; label: string }[] = [
  { step: 1, label: 'Choose groups' },
  { step: 2, label: 'FleetOps review' },
  { step: 3, label: 'Activate SSO' },
];

type StepState = 'done' | 'current' | 'upcoming';

const StepNumber = styled.div<{ stepState: StepState }>`
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 6px;

  ${(props) => {
    if (props.stepState === 'current') {
      return css`
        background-color: var(--color-text-primary);
      `;
    }
    if (props.stepState === 'done') {
      return css`
        background-color: var(--color-status-success-bg);
      `;
    }
    return css`
      border: 1px solid var(--color-grey-7);
    `;
  }}
`;

const Divider = styled.div`
  width: 24px;
  height: 1px;
  margin: 0px 12px;
  background-color: var(--color-grey-7);
`;

const getStepState = (step: ReviewStep, current: ReviewStep): StepState => {
  if (step < current) {
    return 'done';
  }
  return step === current ? 'current' : 'upcoming';
};

/**
 * Where the account is in setting up SSO: the administrator chooses the
 * groups, FleetOps reviews them, then the administrator activates SSO.
 */
const ReviewSteps = ({ current }: { current: ReviewStep }) => (
  <Row centerAlign data-testid="sso-review-steps">
    {STEPS.map(({ step, label }, index) => {
      const stepState = getStepState(step, current);
      return (
        <React.Fragment key={step}>
          {index > 0 && <Divider />}
          <Row centerAlign>
            <StepNumber stepState={stepState}>
              {stepState === 'done' ? (
                <Icon
                  icon="checkmark"
                  width={12}
                  height={12}
                  color="var(--color-status-success)"
                />
              ) : (
                <Typography.Body
                  type="Body 12"
                  color={
                    stepState === 'current'
                      ? 'var(--color-text-inverse)'
                      : 'var(--color-text-tertiary)'
                  }
                >
                  {step}
                </Typography.Body>
              )}
            </StepNumber>
            <Typography.Body
              type="Body 12"
              color={
                stepState === 'upcoming'
                  ? 'var(--color-text-tertiary)'
                  : undefined
              }
            >
              {label}
            </Typography.Body>
          </Row>
        </React.Fragment>
      );
    })}
  </Row>
);

export default ReviewSteps;
